/**
 * Blog Page - Election Information Articles
 * Contains JNE information about Peru's 2026 elections
 */
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faRobot,
    faUser,
    faUsers,
    faBuilding,
    faLandmark,
    faArrowRight,
} from '@fortawesome/free-solid-svg-icons';
import { Button, ThemeToggle } from '@/components/ui';
import { ROUTES } from '@/constants/routes';
import { SEO } from '@/components/features/SEO';
import { useFadeIn } from '@/hooks';
import styles from './Blog.module.css';

export function Blog() {
    const navigate = useNavigate();

    const heroFade = useFadeIn(0.1);
    const primFade = useFadeIn(0.15);
    const electFade = useFadeIn(0.15);
    const cronoFade = useFadeIn(0.15);
    const autoFade = useFadeIn(0.15);
    const reqFade = useFadeIn(0.15);
    const umbFade = useFadeIn(0.15);
    const orgFade = useFadeIn(0.15);

    return (
        <div className={styles.page}>
            <SEO
                title="Blog Peru IA - Cronograma y Datos JNE | Elecciones 2026"
                description="Toda la informacion oficial del JNE sobre las Elecciones Generales Peru 2026. Cronograma, elecciones primarias, candidatos y partidos politicos inscritos."
                url="https://peruhonesto.com/blog"
                keywords={[
                    "cronograma elecciones 2026",
                    "elecciones primarias 2025 peru",
                    "jurado nacional de elecciones",
                    "partidos politicos peru 2026",
                    "jne elecciones 2026",
                    "candidatos presidenciales peru"
                ]}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "Article",
                    "headline": "Elecciones Generales Peru 2026 - Informacion oficial del JNE",
                    "description": "Toda la informacion oficial del JNE sobre las Elecciones Generales Peru 2026.",
                    "url": "https://peruhonesto.com/blog",
                    "image": "https://peruhonesto.com/logo.png",
                    "datePublished": "2025-01-15",
                    "dateModified": "2026-03-04",
                    "author": {
                        "@type": "Organization",
                        "name": "Peru IA",
                        "url": "https://peruhonesto.com"
                    },
                    "publisher": {
                        "@type": "Organization",
                        "name": "Peru IA",
                        "logo": {
                            "@type": "ImageObject",
                            "url": "https://peruhonesto.com/logo.png"
                        }
                    },
                    "inLanguage": "es",
                    "mainEntityOfPage": {
                        "@type": "WebPage",
                        "@id": "https://peruhonesto.com/blog"
                    }
                }}
            />

            {/* Nav */}
            <nav className={styles.nav}>
                <div className={styles.navInner}>
                    <div className={styles.navBrand} onClick={() => navigate(ROUTES.home)}>
                        <img src="/logo.png" alt="Perú IA" className={styles.navLogo} />
                        <span className={styles.navName}>Perú IA</span>
                    </div>
                    <div className={styles.navActions}>
                        <ThemeToggle size="md" />
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => navigate(ROUTES.peruai)}
                        >
                            Ir al Chat
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Button>
                    </div>
                </div>
            </nav>

            {/* Hero */}
            <section
                ref={heroFade.ref}
                className={`${styles.hero} ${heroFade.isVisible ? styles.visible : ''}`}
            >
                <p className={styles.heroLabel}>Blog</p>
                <h1 className={styles.heroTitle}>Elecciones Generales 2026</h1>
                <p className={styles.heroSub}>
                    Información oficial del Jurado Nacional de Elecciones sobre el proceso electoral.
                </p>

                {/* Anchor nav */}
                <div className={styles.anchors}>
                    <a href="#primarias" className={styles.anchorLink}>Primarias</a>
                    <a href="#autoridades" className={styles.anchorLink}>Autoridades</a>
                    <a href="#cronograma" className={styles.anchorLink}>Cronograma</a>
                    <a href="#organizaciones" className={styles.anchorLink}>Partidos</a>
                </div>
            </section>

            {/* Content */}
            <main className={styles.main}>

                {/* Elecciones Primarias */}
                <article
                    id="primarias"
                    ref={primFade.ref}
                    className={`${styles.article} ${primFade.isVisible ? styles.visible : ''}`}
                >
                    <div className={styles.articleHeader}>
                        <div className={styles.articleIcon}>
                            <FontAwesomeIcon icon={faRobot} />
                        </div>
                        <div>
                            <h2 className={styles.articleTitle}>Perú IA</h2>
                            <p className={styles.articleSubtitle}>Elecciones Primarias 2025</p>
                        </div>
                    </div>
                    <div className={styles.articleBody}>
                        <p>
                            Las Elecciones Primarias 2025 son el proceso mediante el cual las organizaciones políticas
                            elegirán las candidaturas que participarán en las Elecciones Generales 2026.
                        </p>
                        <p>
                            Su realización es un requisito obligatorio para la inscripción de fórmulas y listas de candidatos,
                            y tienen como finalidad garantizar que estas se conformen con legitimidad, transparencia y participación.
                        </p>
                    </div>
                </article>

                {/* Electores */}
                <article
                    id="electores"
                    ref={electFade.ref}
                    className={`${styles.article} ${electFade.isVisible ? styles.visible : ''}`}
                >
                    <h2 className={styles.articleTitle}>Electores</h2>
                    <div className={styles.articleBody}>
                        <p>
                            Cada organización política define, de acuerdo con su normativa interna, quiénes participarán
                            como electores en sus Elecciones Primarias. Pueden elegir entre tres modalidades:
                        </p>
                        <ul>
                            <li><strong>Modalidad A:</strong> Afiliados y ciudadanos previamente inscritos como electores,
                                estén o no afiliados a la organización política.</li>
                            <li><strong>Modalidad B:</strong> Solo los afiliados a la organización política. Un afiliado un voto.</li>
                            <li><strong>Modalidad C:</strong> Delegados, quienes deben haber sido elegidos previamente por los afiliados.</li>
                        </ul>
                        <p>
                            Para las elecciones primarias 2025, la mayoría de organizaciones políticas optaron por la modalidad C,
                            votación mediante delegados para elegir a sus candidaturas, solo una organización política eligió la
                            modalidad B, elección mediante afiliados.
                        </p>
                    </div>
                </article>

                {/* Cronograma */}
                <article
                    id="cronograma"
                    ref={cronoFade.ref}
                    className={`${styles.article} ${cronoFade.isVisible ? styles.visible : ''}`}
                >
                    <h2 className={styles.articleTitle}>Cronograma</h2>
                    <div className={styles.articleBody}>
                        <p>
                            Mediante Resolución N° 0126-2025-JNE se aprobó el cronograma electoral para el proceso de las
                            Elecciones Generales 2026, el cual incluye los hitos referidos a las elecciones primarias 2025.
                        </p>
                        <div className={styles.timeline}>
                            <div className={styles.timelineItem}>
                                <div className={styles.timelineDot} />
                                <div className={styles.timelineDate}>18 Feb 2024</div>
                                <div className={styles.timelineText}>
                                    Fecha límite para que las OP entreguen padrones de afiliados
                                </div>
                            </div>
                            <div className={styles.timelineItem}>
                                <div className={styles.timelineDot} />
                                <div className={styles.timelineDate}>19 Oct 2024</div>
                                <div className={styles.timelineText}>
                                    Aprobación del uso de los padrones para elecciones primarias
                                </div>
                            </div>
                            <div className={styles.timelineItem}>
                                <div className={styles.timelineDot} />
                                <div className={styles.timelineDate}>31 Oct 2024</div>
                                <div className={styles.timelineText}>
                                    Fecha límite para inscripción de candidatos en primarias
                                </div>
                            </div>
                            <div className={styles.timelineItem}>
                                <div className={styles.timelineDot} />
                                <div className={styles.timelineDate}>30 Nov 2025</div>
                                <div className={styles.timelineText}>
                                    Elecciones primarias: voto de afiliados y no afiliados
                                </div>
                            </div>
                            <div className={styles.timelineItem}>
                                <div className={styles.timelineDot} />
                                <div className={styles.timelineDate}>07 Dic 2025</div>
                                <div className={styles.timelineText}>
                                    Elecciones primarias: voto de delegados
                                </div>
                            </div>
                        </div>
                    </div>
                </article>

                {/* Autoridades a Elegir */}
                <article
                    id="autoridades"
                    ref={autoFade.ref}
                    className={`${styles.article} ${autoFade.isVisible ? styles.visible : ''}`}
                >
                    <h2 className={styles.articleTitle}>Candidaturas sujetas a elecciones primarias</h2>
                    <div className={styles.articleBody}>
                        <p>
                            Están sujetas a Elecciones Primarias las candidaturas a los siguientes cargos:
                        </p>
                        <div className={styles.positions}>
                            <div className={styles.position}>
                                <div className={styles.positionIcon}>
                                    <FontAwesomeIcon icon={faUser} />
                                </div>
                                <h3>Fórmula presidencial</h3>
                                <p>Presidente y dos vicepresidentes</p>
                            </div>
                            <div className={styles.position}>
                                <div className={styles.positionIcon}>
                                    <FontAwesomeIcon icon={faLandmark} />
                                </div>
                                <h3>Senadores</h3>
                                <p>Representantes del Senado</p>
                            </div>
                            <div className={styles.position}>
                                <div className={styles.positionIcon}>
                                    <FontAwesomeIcon icon={faUsers} />
                                </div>
                                <h3>Diputados</h3>
                                <p>Representantes de Diputados</p>
                            </div>
                            <div className={styles.position}>
                                <div className={styles.positionIcon}>
                                    <FontAwesomeIcon icon={faBuilding} />
                                </div>
                                <h3>Parlamento Andino</h3>
                                <p>Representantes ante el Parlamento Andino</p>
                            </div>
                        </div>
                    </div>
                </article>

                {/* Requisitos */}
                <article
                    ref={reqFade.ref}
                    className={`${styles.article} ${reqFade.isVisible ? styles.visible : ''}`}
                >
                    <h2 className={styles.articleTitle}>Requisitos para postular</h2>
                    <div className={styles.articleBody}>
                        <ul>
                            <li>Cada organización política determina los requisitos, la forma de presentación de candidaturas
                                y el número de postulantes de acuerdo con su normativa interna y en cumplimiento de las normas electorales.</li>
                            <li>Las personas candidatas en las Elecciones Primarias deben haberse afiliado a la organización
                                política hasta el <strong>12 de julio de 2024</strong>.</li>
                            <li>El Órgano Electoral Central de la organización política es responsable de comunicar a la ONPE
                                las candidaturas definitivas y los cargos a los que postulen en las Elecciones Primarias como
                                máximo hasta el <strong>7 de noviembre de 2025</strong>.</li>
                        </ul>
                    </div>
                </article>

                {/* Umbral */}
                <article
                    ref={umbFade.ref}
                    className={`${styles.article} ${umbFade.isVisible ? styles.visible : ''}`}
                >
                    <h2 className={styles.articleTitle}>Umbral de las elecciones primarias</h2>
                    <div className={styles.articleBody}>
                        <p>
                            Para que las organizaciones políticas puedan continuar en la carrera electoral en sus Elecciones
                            Primarias deben haber votado válidamente, al menos:
                        </p>
                        <ul>
                            <li>El <strong>10% de los electores</strong> del padrón de la organización política correspondiente
                                (para la modalidad A y B).</li>
                            <li>El <strong>10% de los delegados</strong> electos (para la modalidad C).</li>
                        </ul>
                        <p>
                            Una vez concluida la etapa de las Elecciones Primarias, las organizaciones políticas que hayan
                            superado el umbral electoral podrán inscribir, ante los Jurados Electorales Especiales correspondientes,
                            a sus candidatos para la Elecciones Generales 2026.
                        </p>
                    </div>
                </article>

                {/* Organizaciones Políticas */}
                <article
                    id="organizaciones"
                    ref={orgFade.ref}
                    className={`${styles.article} ${orgFade.isVisible ? styles.visible : ''}`}
                >
                    <h2 className={styles.articleTitle}>Organizaciones Políticas Inscritas</h2>
                    <div className={styles.articleBody}>
                        <div className={styles.orgHeader}>
                            <span className={styles.orgNumber}>36</span>
                            <span className={styles.orgLabel}>partidos y alianzas electorales inscritas</span>
                        </div>
                        <div className={styles.orgGrid}>
                            {[
                                "Ahora Nación - AN",
                                "Alianza Electoral Venceremos",
                                "Alianza para el Progreso",
                                "Alianza País - Partido de Integración Social",
                                "Fe en el Perú",
                                "Fuerza Popular",
                                "Fuerza y Libertad",
                                "Juntos por el Perú",
                                "Libertad Popular",
                                "Partido Aprista Peruano",
                                "Partido Cívico Obras",
                                "Partido de los Trabajadores y Empredendores PTE-Perú",
                                "Partido del Buen Gobierno",
                                "Partido Demócrata Unido Perú",
                                "Partido Demócrata Verde",
                                "Partido Democrático Federal",
                                "Partido Democrático Somos Perú",
                                "Partido Frente de la Esperanza 2021",
                                "Partido Morado",
                                "Partido País para Todos",
                                "Partido Patriótico del Perú",
                                "Partido Político Cooperacion Popular",
                                "Partido Político Integridad Democrática",
                                "Partido Político Nacional Perú Libre",
                                "Partido Político Perú Acción",
                                "Partido Político Perú Primero",
                                "Partido Político PRIN",
                                "Partido SiCreo",
                                "Perú Moderno",
                                "Podemos Perú",
                                "Progresemos",
                                "Renovación Popular",
                                "Salvemos al Perú",
                                "Un Camino Diferente",
                                "Unidad Nacional",
                                "Frente Popular Agrícola FIA del Perú",
                            ].map((name) => (
                                <div key={name} className={styles.orgItem}>{name}</div>
                            ))}
                        </div>
                    </div>
                </article>
            </main>

            {/* Footer */}
            <footer className={styles.footer}>
                <p>Perú IA · Proyecto independiente para promover el voto informado</p>
                <p>Información oficial del Jurado Nacional de Elecciones (JNE)</p>
                <div className={styles.footerLinks}>
                    <a
                        href="https://www.instagram.com/peruhonesto/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Instagram
                    </a>
                    <a
                        href="https://www.tiktok.com/@peruia"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        TikTok
                    </a>
                    <a href={ROUTES.about}>Sobre nosotros</a>
                    <a href={ROUTES.privacy}>Privacidad</a>
                    <a href={ROUTES.terms}>Términos</a>
                </div>
                <p className={styles.footerDisclaimer}>
                    Esta plataforma no hace recomendaciones de voto. Su objetivo es proporcionar
                    información verificada para que cada ciudadano tome su propia decisión.
                </p>
            </footer>
        </div>
    );
}
